import React from "react";
import {
    ListTableColumn,
    createListTableColumns,
    ensure,
    mapToCodedProperty,
    depascalize,
    Stack,
    Text,
    Link
} from "sprint-common-web";

import { NoteItem } from "../../../../common/components/notes/hooks/state/NoteItem";
import { DynamicSubtitleType } from "../../../../common/types/DynamicSubtitleType";
import { getLanguageDisplayName } from "../../../../common/types/Language";
import { NoteSubjectType } from "../../../../common/types/NoteSubjectType";
import { RecordType } from "../../../../common/types/RecordType";
import { renderNotes } from "../../../../common/utils/renderNotes";
import { LanguageAssetInfo } from "../../../common/components/listings/AssetsAvailabilityList";
import { VideoInfo } from "../../../common/components/listings/VideoInfo";
import { OrderAssetAvailability } from "../../../common/types/OrderAssetAvailability";
import { OrderAssetStorageType } from "../../../common/types/OrderAssetStorageType";
import { stringifyOrderResolutionDimensions } from "../../../common/types/OrderResolutionDimensions";
import { renderNoteBadge } from "../../../common/utils/renderNoteBadge";
import { VideoFileType, AudioFileType } from "../OrderDashboardItem";
import {
    AssetsAvailabilityItem,
    VideoAssetAvailabilityItem,
    SoundtrackAssetAvailabilityItem,
    SubtitleAssetAvailabilityItem
} from "./AssetsAvailabilityItem";

export const createOrderAssetsSectionColumns = ({
    areNotesLoading
}: {
    areNotesLoading?: boolean;
}): ListTableColumn<AssetsAvailabilityItem>[] => {
    const renderAvailability = (item?: AssetsAvailabilityItem) => {
        ensure(item);

        switch (item.type) {
            case RecordType.Video:
                return renderVideoAvailability(item);
            case RecordType.Audio:
                return renderSoundtrackAvailability(item);
            case RecordType.Subtitle:
                return renderSubtitleAvailability(item);
            default:
                throw new Error("Unsupported item type");
        }
    };

    return createListTableColumns<AssetsAvailabilityItem>([
        {
            type: "basic",
            key: "type",
            name: "Type",
            minWidth: 38,
            maxWidth: 60,
            onRender: renderType
        },
        {
            type: "basic",
            key: "name",
            name: "Name",
            minWidth: 100,
            maxWidth: 180,
            onRender: renderName
        },
        {
            type: "basic",
            key: "type",
            name: "Availability",
            minWidth: 80,
            maxWidth: 80,
            onRender: renderAvailability
        },
        {
            type: "basic",
            key: "bucketName",
            name: "Bucket",
            minWidth: 70,
            maxWidth: 210,
            onRender: renderBucketName
        },
        {
            type: "basic",
            key: "filePath",
            name: "Path",
            minWidth: 70,
            maxWidth: 350,
            onRender: renderFilePath
        },
        {
            type: "basic",
            key: "fileType",
            name: "File type",
            minWidth: 40,
            maxWidth: 100,
            onRender: renderFileType
        },
        {
            type: "basic",
            key: "fileType",
            name: "Details",
            minWidth: 80,
            maxWidth: 180,
            onRender: renderDetails
        },
        {
            type: "basic",
            key: "notes",
            name: "Notes",
            minWidth: 120,
            maxWidth: 120,
            isResizable: false,
            onRender: (item) =>
                renderNotes({
                    subjectType: NoteSubjectType.CloudAsset,
                    notes: item?.notes ?? [],
                    itemId: item?.assetInformation.assetId,
                    renderBadge: (noteItem: NoteItem) => renderNoteBadge(noteItem),
                    isLoading: areNotesLoading
                })
        }
    ]);
};

const renderName = (item?: AssetsAvailabilityItem) => {
    ensure(item);

    const href =
        item.assetInformation?.assetHref && item.assetInformation.availability === OrderAssetAvailability.FullMatch
            ? "/#" + item.assetInformation?.assetHref
            : undefined;

    if (item.type === RecordType.Video) {
        return (
            <Link href={href} disabled={!href}>
                <Text tooltip={item.name} variant="small">
                    {item.name}
                </Text>
            </Link>
        );
    }

    return (
        <Link href={href} disabled={!href}>
            <Text
                tooltip={item.name}
                variant="small"
                key={item.assetInformation.id}
                value={getLanguageDisplayName(
                    mapToCodedProperty({
                        id: item.assetInformation.id,
                        name: item.name,
                        code: item.assetInformation.code
                    })
                )}
            />
        </Link>
    );
};

const renderType = (item?: AssetsAvailabilityItem) => {
    ensure(item);
    return <Text variant="small">{RecordType[item.type]}</Text>;
};

const renderVideoAvailability = (item: VideoAssetAvailabilityItem) => {
    return (
        <VideoInfo
            availability={item.assetInformation.availability}
            requestedAspectRatio={item.aspectRatio}
            nonMatchingVideos={item.nonMatchingVideos}
            type={item.assetInformation.storageType ?? OrderAssetStorageType.Cloud}
            requestedResolution={item.targetResolution}
            testId="availability-indicator"
        />
    );
};

const renderSoundtrackAvailability = (item: SoundtrackAssetAvailabilityItem) => {
    return (
        <LanguageAssetInfo
            assetType={RecordType.Audio}
            item={{
                availability: item.assetInformation.availability,
                language: { id: item.assetInformation.id, name: item.name, code: item.assetInformation.code }
            }}
            storageType={item.assetInformation.storageType ?? OrderAssetStorageType.Cloud}
            testId="availability-indicator"
        />
    );
};

const renderSubtitleAvailability = (item: SubtitleAssetAvailabilityItem) => {
    return (
        <LanguageAssetInfo
            assetType={RecordType.Subtitle}
            item={{
                availability: item.assetInformation.availability,
                language: { id: item.assetInformation.id, name: item.name, code: item.assetInformation.code }
            }}
            storageType={item.assetInformation.storageType ?? OrderAssetStorageType.Cloud}
            testId="availability-indicator"
        />
    );
};

const renderFilePath = (item?: AssetsAvailabilityItem) => {
    ensure(item);

    return <Text variant="small" tooltip={item.filePath} value={item.filePath} />;
};

const renderBucketName = (item?: AssetsAvailabilityItem) => {
    ensure(item);

    return <Text variant="small" tooltip={item.bucketName} value={item.bucketName} />;
};

const renderFileType = (item?: AssetsAvailabilityItem) => {
    ensure(item);

    switch (item.type) {
        case RecordType.Video:
            return <Text variant="small" value={item.fileType ? depascalize(VideoFileType[item.fileType]) : "-"} />;

        case RecordType.Audio:
            return <Text variant="small" value={item.fileType ? depascalize(AudioFileType[item.fileType]) : "-"} />;

        case RecordType.Subtitle:
            return (
                <Text variant="small" value={item.fileType ? depascalize(DynamicSubtitleType[item.fileType]) : "-"} />
            );

        default:
            throw new Error("Unsupported file type");
    }
};

const renderDetails = (item?: AssetsAvailabilityItem) => {
    ensure(item);

    if (item.type !== RecordType.Video) return <Text>-</Text>;

    if (!item.subtitle && !item.aspectRatio && !item.sourceResolution) return <Text>-</Text>;

    return (
        <Stack>
            {!!item.subtitle && <Text variant="small">{getLanguageDisplayName(item.subtitle)}</Text>}
            {!!item.aspectRatio && <Text variant="small">{item.aspectRatio?.name}</Text>}
            {!!item.sourceResolution && (
                <Text variant="small">{stringifyOrderResolutionDimensions(item.sourceResolution)}</Text>
            )}
        </Stack>
    );
};
